import { ActionIcon, Group, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import type { ReasonFormValues } from 'app/Private/Reasons/ReasonForm/ReasonForm';
import { ReasonForm } from 'app/Private/Reasons/ReasonForm/ReasonForm';
import { i18nKeys, useTranslation } from 'locales';
import { useSelector } from 'react-redux';
import type { StoreState } from 'types/storeTypes';
import {
  type Reason,
  ReasonTypeSchema,
  useDeleteReason,
  useUpdateReason,
} from '~/modules/Settings/Variables/Reasons/api';

type ReasonListItemProps = {
  item: Reason;
};

export const ReasonListItem = ({ item }: ReasonListItemProps) => {
  const { t } = useTranslation();

  const { reason_types } = useSelector((state: StoreState) => state.app.constants);

  const { deleteReason } = useDeleteReason();
  const { updateReason } = useUpdateReason();

  const { id, name, reasonType, isDeletable } = item;

  const handleUpdate = () =>
    modals.open({
      size: 'sm',
      title: t(i18nKeys.EDIT_REASON),
      children: (
        <ReasonForm
          reason={item}
          onCancel={() => modals.closeAll()}
          onSubmit={(data: ReasonFormValues) =>
            updateReason(
              {
                id,
                data: { name: data.name, reasonType: ReasonTypeSchema.parse(data.reason_type) },
              },
              { onSuccess: () => modals.closeAll() },
            )
          }
        />
      ),
    });

  const handleDelete = () =>
    modals.openConfirmModal({
      title: t(i18nKeys.CONFIRMATION),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.AYS.DELETE_REASON)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteReason({ id }),
    });

  return (
    <Table.Tr>
      <Table.Td>{id}</Table.Td>
      <Table.Td>{name}</Table.Td>
      <Table.Td>{reason_types.find(option => option.value === reasonType)?.description}</Table.Td>
      <Table.Td>
        {t(
          !isDeletable ? i18nKeys.SETTINGS.TASKS.TASK_DEFAULT : i18nKeys.SETTINGS.TASKS.TASK_CUSTOM,
        )}
      </Table.Td>
      <Table.Td>
        {isDeletable && (
          <Group gap="xs" wrap="nowrap">
            <ActionIcon variant="subtle" aria-label={t(i18nKeys.EDIT)} onClick={handleUpdate}>
              <IconPencil size={18} stroke={1.5} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label={t(i18nKeys.DELETE)}
              onClick={handleDelete}
            >
              <IconTrash size={18} stroke={1.5} />
            </ActionIcon>
          </Group>
        )}
      </Table.Td>
    </Table.Tr>
  );
};
